import { PushNotifications } from '@capacitor/push-notifications';
import { backendPost } from '@/lib/backendClient';
import { ensurePushPermission } from './nativeCapabilities';
import { isNativeMobileApp } from './platform';

const PUSH_TOKEN_KEY = 'correfood_push_token';
const MOBILE_APP_ID = 'br.com.correfood.app';
const COURIER_PUSH_CHANNEL_ID = 'courier_orders_audio_v1';
const COURIER_PUSH_CHANNEL_SOUND = 'courier_alert.mp3';

let listenersReady = false;
let listenerHandles = [];

function canUseWebStorage() {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

function dispatchRouteChange() {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(new PopStateEvent('popstate'));
  } catch {
    window.dispatchEvent(new Event('popstate'));
  }
}

function getStoredPushToken() {
  if (!canUseWebStorage()) return '';
  return String(window.localStorage.getItem(PUSH_TOKEN_KEY) || '').trim();
}

function setStoredPushToken(token) {
  if (!canUseWebStorage()) return;
  if (!token) {
    window.localStorage.removeItem(PUSH_TOKEN_KEY);
    return;
  }
  window.localStorage.setItem(PUSH_TOKEN_KEY, token);
}

function resolveNotificationRoute(data) {
  const explicitRoute = String(data?.route || '').trim();
  if (explicitRoute.startsWith('/')) return explicitRoute;

  const role = String(data?.target_role || '').trim().toLowerCase();
  if (role === 'courier') return '/entregas';
  if (role === 'merchant') return '/logista';
  if (role === 'user') return '/perfil?tab=orders';
  return '/';
}

function attachOrderParam(route, orderId) {
  const normalizedRoute = String(route || '/').trim() || '/';
  const normalizedOrderId = String(orderId || '').trim();
  if (!normalizedOrderId || typeof window === 'undefined') return normalizedRoute;

  try {
    const parsed = new URL(normalizedRoute, window.location.origin);
    if (!parsed.searchParams.get('order_id')) {
      parsed.searchParams.set('order_id', normalizedOrderId);
    }
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    if (normalizedRoute.includes('?')) {
      return `${normalizedRoute}&order_id=${encodeURIComponent(normalizedOrderId)}`;
    }
    return `${normalizedRoute}?order_id=${encodeURIComponent(normalizedOrderId)}`;
  }
}

function navigateFromPushData(data) {
  if (typeof window === 'undefined') return;
  const route = attachOrderParam(resolveNotificationRoute(data), data?.order_id);
  const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  if (current === route) return;
  window.history.pushState({}, '', route);
  dispatchRouteChange();
}

async function registerTokenOnBackend(token) {
  const normalizedToken = String(token || '').trim();
  if (!normalizedToken) return;

  const sessionToken = canUseWebStorage()
    ? String(window.localStorage.getItem('correfood_token') || '').trim()
    : '';
  if (!sessionToken) return;

  await backendPost('/api/mobile/push/devices/register', {
    token: normalizedToken,
    platform: 'android',
    app_id: MOBILE_APP_ID,
  });
}

async function ensurePushListeners() {
  if (!isNativeMobileApp() || listenersReady) return;

  listenersReady = true;
  listenerHandles.push(
    await PushNotifications.addListener('registration', async (token) => {
      const value = String(token?.value || '').trim();
      if (!value) return;
      setStoredPushToken(value);
      try {
        await registerTokenOnBackend(value);
      } catch {
        // Mantem melhor-esforco para nao quebrar login/navegacao.
      }
    }),
  );

  listenerHandles.push(
    await PushNotifications.addListener('registrationError', (error) => {
      console.warn('[PUSH] registrationError', error);
    }),
  );

  listenerHandles.push(
    await PushNotifications.addListener('pushNotificationActionPerformed', (event) => {
      const payload = event?.notification?.data || {};
      navigateFromPushData(payload);
    }),
  );
}

async function ensureAndroidPushChannels() {
  if (!isNativeMobileApp()) return;

  try {
    await PushNotifications.createChannel({
      id: COURIER_PUSH_CHANNEL_ID,
      name: 'Pedidos Motoboy CorreFood',
      description: 'Alertas de novos pedidos para motoboy com aviso sonoro',
      sound: COURIER_PUSH_CHANNEL_SOUND,
      importance: 5,
      visibility: 1,
      vibration: true,
    });
  } catch {
    // Melhor-esforco: nao bloqueia registro de push.
  }
}

export async function activateNativePushNotifications() {
  if (!isNativeMobileApp()) return;

  await ensurePushListeners();
  await ensureAndroidPushChannels();
  const permission = await ensurePushPermission();
  if (permission?.receive !== 'granted') return;
  await PushNotifications.register();
}

export async function deactivateNativePushNotifications() {
  if (!isNativeMobileApp()) return;

  const token = getStoredPushToken();
  if (!token) return;

  try {
    await backendPost('/api/mobile/push/devices/unregister', { token });
  } catch {
    // Melhor-esforco para logout/troca de conta.
  } finally {
    setStoredPushToken('');
  }
}

export async function resetNativePushListeners() {
  if (!listenersReady) return;
  await Promise.all(
    listenerHandles.map(async (handle) => {
      if (!handle || typeof handle.remove !== 'function') return;
      try {
        await handle.remove();
      } catch {
        // noop
      }
    }),
  );
  listenerHandles = [];
  listenersReady = false;
}
